import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles, Users, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setError("Missing invite token"); return; }
    api.get(`/teams/invites/${token}`)
      .then((r) => setInvite(r.data))
      .catch((err) => setError(err?.response?.data?.detail || "Invite not found"));
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const res = await api.post("/teams/invites/accept", { token });
      toast.success(`Joined ${res.data.team_name}`);
      // Refresh user
      const me = await api.get("/auth/me");
      localStorage.setItem("gf_user", JSON.stringify(me.data));
      navigate("/dashboard");
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to accept invite");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) return null;

  const renderInviteAction = () => {
    if (!user) {
      return (
        <div className="mt-8 space-y-3">
          <p className="text-sm text-[#1A1A1A]">Sign in or create an account with <b>{invite.email}</b> to accept.</p>
          <div className="flex gap-2">
            <Link to={`/login?next=/accept-invite?token=${token}`} className="flex-1">
              <Button variant="outline" className="w-full border-[#E6E4DF]" data-testid="accept-invite-login">Sign in</Button>
            </Link>
            <Link to={`/signup?next=/accept-invite?token=${token}&email=${encodeURIComponent(invite.email)}`} className="flex-1">
              <Button className="w-full bg-[#1C3F35] text-white hover:bg-[#1A362D]" data-testid="accept-invite-signup">Create account</Button>
            </Link>
          </div>
        </div>
      );
    }
    if (user.email !== invite.email) {
      return (
        <div className="mt-6 rounded-md bg-[#C1654B]/10 border border-[#C1654B]/30 p-4 text-sm text-[#C1654B]">
          This invite is for <b>{invite.email}</b>, but you're signed in as <b>{user.email}</b>. Sign out and log in with the invited address.
        </div>
      );
    }
    return (
      <Button
        onClick={accept}
        disabled={accepting}
        data-testid="accept-invite-btn"
        className="mt-8 w-full bg-[#1C3F35] text-white hover:bg-[#1A362D] py-6"
      >
        {accepting ? <Loader2 className="w-4 h-4 animate-spin"/> : <><CheckCircle2 className="w-4 h-4 mr-2"/>Accept invite</>}
      </Button>
    );
  };

  const renderBody = () => {
    if (error) {
      return (
        <>
          <h1 className="font-heading text-2xl text-[#C1654B]">{error}</h1>
          <p className="text-sm text-[#5E5E5E] mt-2">Ask the person who invited you to send a fresh link.</p>
        </>
      );
    }
    if (!invite) {
      return <div className="text-[#5E5E5E]"><Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading invite…</div>;
    }
    return (
      <>
        <Users className="w-8 h-8 text-[#1C3F35]"/>
        <h1 className="font-heading font-light text-3xl mt-4 tracking-tight">
          You've been invited to <span className="text-[#1C3F35]">{invite.team_name}</span>
        </h1>
        <p className="text-[#5E5E5E] mt-3 text-sm">
          <b>{invite.inviter_name}</b> invited <b>{invite.email}</b> to collaborate on grant applications.
        </p>
        {renderInviteAction()}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6" data-testid="accept-invite-page">
      <div className="max-w-md w-full bg-white border border-[#E6E4DF] rounded-lg p-10">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-md bg-[#1C3F35] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5}/>
          </div>
          <span className="font-heading text-lg">Beacon of Grants</span>
        </Link>

        {renderBody()}
      </div>
    </div>
  );
}
